import {
    centsToFormattedDollars,
    formatWithoutTimezone,
    isoDateToUTCTimezoneDate,
} from '@fruitful/fruitils';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useMemo } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteRegular,
    FullScreenSpinner,
    H6Regular,
    InfoIcon,
    NavigationTile,
    ScreenContainer,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import { captureException, captureWarning } from '../../../logger/Logger';
import useGetICTTradingDay from '../../hooks/useGetICTTradingDay';
import {
    ReadableRetirementContributionType,
    RetirementDistributionCreateType,
    type TestICTWithdrawalRequest,
    useRequestICTWithdrawal,
} from '../../hooks/useTestICTWithdrawal';
import { apexWithdrawalRequestAtom } from '../../state/investmentAtoms';

const WithdrawalItem = (item: { description: string; value: string }) => {
    return (
        <XStack
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            padding={16}
        >
            <BodyMediumRegular
                color="$gray1000"
                flex={1}
                flexShrink={1}
                minWidth={0}
            >
                {item.description}
            </BodyMediumRegular>
            <BodyMediumEmphasized textAlign="right" flex={2} flexShrink={0}>
                {item.value}
            </BodyMediumEmphasized>
        </XStack>
    );
};

export const WithdrawalReview = () => {
    const toast = useToastController();
    const queryClient = useQueryClient();

    const apexWithdrawalRequest = useAtomValue(apexWithdrawalRequestAtom);
    const { mutateAsync, isPending } = useRequestICTWithdrawal({});

    const { data: tradingDayData, isLoading: isTradingDayLoading } =
        useGetICTTradingDay({});

    const formattedTradingDay = useMemo(() => {
        if (tradingDayData?.tradingDay == null) return '';
        const date = isoDateToUTCTimezoneDate(tradingDayData.tradingDay);
        if (isNaN(date.getTime())) return '';
        return formatWithoutTimezone(date, 'MMMM d, yyyy');
    }, [tradingDayData]);

    const isValid = useMemo(() => {
        if (apexWithdrawalRequest === null) {
            return false;
        }

        const {
            amountCents,
            fromAccount,
            toAccount,
            isRetirementAccount,
            distributionType,
        } = apexWithdrawalRequest;

        if (toAccount == null) {
            return false;
        }

        if (fromAccount == null) {
            return false;
        }

        // Apex rejects TYPE_UNSPECIFIED. The removed "Other" option sent that sentinel.
        if (
            isRetirementAccount &&
            (distributionType == null ||
                distributionType ===
                    RetirementDistributionCreateType.TYPE_UNSPECIFIED)
        ) {
            return false;
        }

        return !(amountCents == null || amountCents <= 0);
    }, [apexWithdrawalRequest]);

    const ictWithdrawalRequest = useMemo(():
        | TestICTWithdrawalRequest
        | undefined => {
        if (apexWithdrawalRequest === null) {
            return undefined;
        }

        const {
            amountCents,
            fromAccount,
            toAccount,
            isRetirementAccount,
            distributionType,
            liquidateAccount,
        } = apexWithdrawalRequest;

        if (fromAccount == null || toAccount == null) {
            return undefined;
        }

        if (
            isRetirementAccount &&
            (distributionType == null ||
                distributionType ===
                    RetirementDistributionCreateType.TYPE_UNSPECIFIED)
        ) {
            return undefined;
        }

        return {
            fromApexAccountId: fromAccount.apexAccountId,
            toSyncteraAccountId: toAccount.id,
            ...(!liquidateAccount && { amountCents }),
            fullDisbursement: liquidateAccount,
            memo: `${fromAccount.nickname} withdrawal to ${toAccount.nickname}`,
            ...(isRetirementAccount &&
                distributionType != null && {
                    retirementDistribution: {
                        type: distributionType,
                    },
                }),
        };
    }, [apexWithdrawalRequest]);

    if (isPending || isTradingDayLoading) {
        return <FullScreenSpinner />;
    }

    if (apexWithdrawalRequest === null) {
        return <Redirect href="/home/investment" />;
    }

    const {
        amountCents,
        fromAccount,
        toAccount,
        isRetirementAccount,
        distributionType,
    } = apexWithdrawalRequest;

    return (
        <ScreenContainer
            onFallbackRoute="/home/investment"
            hasBackButton={true}
            title="Review details"
        >
            <YStack flexGrow={1} gap={24} width="100%">
                <H6Regular>Review transfer details</H6Regular>
                <BodyMediumRegular color="$gray1000">
                    Please review the information below. A Fruitful team member
                    may reach out if we need any additional information.
                </BodyMediumRegular>
                <YStack
                    width="100%"
                    backgroundColor="$gray200"
                    borderRadius={16}
                >
                    <XStack
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        padding={16}
                    >
                        <BodyMediumRegular
                            color="$gray1000"
                            flex={1}
                            flexShrink={1}
                            minWidth={0}
                        >
                            From
                        </BodyMediumRegular>
                        <YStack alignItems="flex-end" flex={2} flexShrink={0}>
                            <BodyMediumEmphasized textAlign="right">
                                {fromAccount?.nickname ??
                                    'Please select an investment account'}
                            </BodyMediumEmphasized>
                            {fromAccount != null && (
                                <FootnoteRegular color="$gray1000">
                                    {fromAccount.accountNumber}
                                </FootnoteRegular>
                            )}
                        </YStack>
                    </XStack>
                    <XStack
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        padding={16}
                    >
                        <BodyMediumRegular
                            color="$gray1000"
                            flex={1}
                            flexShrink={1}
                            minWidth={0}
                        >
                            To
                        </BodyMediumRegular>
                        <YStack alignItems="flex-end" flex={2} flexShrink={0}>
                            <BodyMediumEmphasized textAlign="right">
                                {toAccount?.nickname ??
                                    'Please select a bank account'}
                            </BodyMediumEmphasized>
                            {toAccount != null && (
                                <FootnoteRegular color="$gray1000">
                                    {toAccount.bankName} *
                                    {getLastFourAccountNumber(
                                        toAccount.accountNumberMasked,
                                    )}
                                </FootnoteRegular>
                            )}
                        </YStack>
                    </XStack>
                    {isRetirementAccount && (
                        <WithdrawalItem
                            description="Distribution type"
                            value={
                                distributionType != null &&
                                distributionType !==
                                    RetirementDistributionCreateType.TYPE_UNSPECIFIED
                                    ? ReadableRetirementContributionType[
                                          distributionType
                                      ]
                                    : 'Please select a distribution reason'
                            }
                        />
                    )}
                    <WithdrawalItem
                        description="Date"
                        value={formattedTradingDay}
                    />
                    <WithdrawalItem
                        description="Processing time"
                        value="4 - 6 business days"
                    />
                    <WithdrawalItem
                        description="Amount"
                        value={centsToFormattedDollars(amountCents ?? 0)}
                    />
                </YStack>
                <NavigationTile
                    tone="positive"
                    title="Selling investments to fund this transfer may create taxable gains. These could increase your tax bill for the year."
                    iconLeft={InfoIcon}
                />
                <FootnoteRegular color="$gray1000">
                    Please note: For full account liquidations, the actual
                    amount transferred may differ from the requested amount
                    shown above due to market price fluctuations between the
                    time of sale and settlement.
                </FootnoteRegular>
                <FootnoteRegular
                    color="$gray1000"
                    textAlign="center"
                    alignSelf="center"
                >
                    By confirming, I authorize Fruitful to initiate the transfer
                    as stated above.
                </FootnoteRegular>
                <Button
                    disabled={!isValid}
                    text="Confirm transfer"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    onPress={async () => {
                        await mutateAsync(
                            {
                                body: ictWithdrawalRequest,
                            },
                            {
                                onSuccess: (response) => {
                                    if (response.success) {
                                        const apexAccountId =
                                            ictWithdrawalRequest?.fromApexAccountId;

                                        if (apexAccountId != null) {
                                            void queryClient.invalidateQueries({
                                                queryKey: [
                                                    `/api/investments/${apexAccountId}/transactions`,
                                                ],
                                            });
                                        }

                                        router.navigate(
                                            '/investments/transfer/withdrawal-submitted',
                                        );
                                    } else {
                                        captureWarning(
                                            'TODO ICT withdrawal request error',
                                        );
                                        toast.show('Something went wrong');
                                    }
                                },
                                onError: (error) => {
                                    captureException(error);
                                    toast.show('Something went wrong');
                                },
                            },
                        );
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
};
